import type { RecognitionScheduleDraft, RecognitionScheduleEntry, RecognitionScheduleWorkspace } from "@chro/shared";
import { draftForEntry, itemTradeIds, scheduleDraftError, type ScheduleItem } from "./recognition-schedules";

/** Presentation metadata only: all recognition amounts come from the server. */
export type WorkspaceTrade = RecognitionScheduleWorkspace["trades"][number];

export interface RecognitionTreeNode {
  id: string;
  kind: "book" | "pair" | "contract" | "trade";
  label: string;
  tradeIds: string[];
  children: RecognitionTreeNode[];
  buyQuantity: number | null;
  sellQuantity: number | null;
  quantityUnit: string | null;
  matchedQuantity: number | null;
  openBuyQuantity: number | null;
  openSellQuantity: number | null;
  netOpenQuantity: number | null;
  buyPrice: number | null;
  sellPrice: number | null;
  currency: string | null;
  priceUnit: string | null;
  trade?: WorkspaceTrade;
}

// JSON tuple encoding distinguishes missing labels, literal display labels and
// delimiters in names. The leaf identity does not depend on any display label.
const nodeId = (kind: RecognitionTreeNode["kind"], identity: unknown[]) => JSON.stringify([kind, ...identity]);
const text = (value: string | null | undefined): string | null => value?.trim() || null;
const knownPriceUnit = (value: string | undefined): string | null => {
  const unit = text(value);
  return unit && !/unknown|unavailable/i.test(unit) ? unit : null;
};
const unique = <T>(values: T[]): T[] => [...new Set(values)];
const sum = (values: number[]): number => values.reduce((total, value) => total + value, 0);

function contractIdentity(trade: WorkspaceTrade): unknown[] {
  return [
    trade.contract ? [trade.contract.code, trade.contract.expYear, trade.contract.expMonth] : [trade.product, trade.period],
    text(trade.currency), text(trade.priceUnit), text(trade.quantityUnit), trade.origin,
  ];
}

export function recognitionTradeLabel(trade: WorkspaceTrade): string {
  return `${trade.side === "B" ? "Buy" : "Sell"} · ${trade.product} · ${trade.period}`;
}

export function recognitionQuantityLabel(trade: WorkspaceTrade): string {
  return text(trade.quantityUnit) ?? "Quantity unit unavailable";
}

export function recognitionPriceLabel(trade: WorkspaceTrade): string {
  const currency = text(trade.currency), unit = knownPriceUnit(trade.priceUnit);
  if (!currency || !unit) return "Price currency or unit unavailable";
  // p/therm and similar units already carry their currency scale. Do not turn
  // a price in pence into a misleading GBP/therm label.
  if (/^(?:p|pence|ct|cents)\//i.test(unit)) return `${unit} (${currency})`;
  return unit.startsWith(`${currency}/`) ? unit : `${currency}/${unit.replace(/^\//, "")}`;
}

function metrics(trades: WorkspaceTrade[], showPrices: boolean): Pick<RecognitionTreeNode,
  "buyQuantity" | "sellQuantity" | "quantityUnit" | "matchedQuantity" | "openBuyQuantity" | "openSellQuantity" | "netOpenQuantity" | "buyPrice" | "sellPrice" | "currency" | "priceUnit"> {
  const units = unique(trades.map((trade) => text(trade.quantityUnit)));
  const commonUnit = units.length === 1 ? units[0]! : null;
  const sameContract = unique(trades.map((trade) => JSON.stringify(contractIdentity(trade)))).length === 1;
  const quantityUnit = commonUnit && commonUnit !== "source units" && !/unknown|unavailable/i.test(commonUnit)
    && (commonUnit === "MW" || sameContract) ? commonUnit : null;
  const quantityKnown = quantityUnit !== null;
  const sideTrades = (side: "B" | "S") => trades.filter((trade) => trade.side === side);
  const buyQuantity = quantityKnown ? sum(sideTrades("B").map((trade) => trade.quantity)) : null;
  const sellQuantity = quantityKnown ? sum(sideTrades("S").map((trade) => trade.quantity)) : null;
  const openKnown = quantityKnown && trades.every((trade) => trade.openQuantity != null);
  const openBuyQuantity = openKnown ? sum(sideTrades("B").map((trade) => trade.openQuantity!)) : null;
  const openSellQuantity = openKnown ? sum(sideTrades("S").map((trade) => trade.openQuantity!)) : null;
  // A matched slice appears on both execution legs. At group level, summing
  // those quantities would double count it and can also miss an outside leg.
  // Display matched quantity only for an individual execution; the Matching
  // panel has the canonical source-item quantities for a group.
  const matchedQuantity = trades.length === 1 && quantityKnown ? trades[0]!.matchedQuantity ?? null : null;
  const comparable = showPrices && quantityKnown && trades.every((trade) => !!text(trade.currency) && !!knownPriceUnit(trade.priceUnit)) && sameContract;
  const weightedPrice = (side: "B" | "S") => {
    const rows = sideTrades(side), quantity = sum(rows.map((trade) => trade.quantity));
    return comparable && rows.length > 0 && quantity > 0 ? sum(rows.map((trade) => trade.price * trade.quantity)) / quantity : null;
  };
  return { buyQuantity, sellQuantity, quantityUnit, matchedQuantity, openBuyQuantity, openSellQuantity,
    netOpenQuantity: openBuyQuantity == null || openSellQuantity == null ? null : openBuyQuantity - openSellQuantity,
    buyPrice: weightedPrice("B"), sellPrice: weightedPrice("S"),
    currency: comparable ? text(trades[0]!.currency) : null, priceUnit: comparable ? text(trades[0]!.priceUnit) : null };
}

function groupBy<T>(rows: T[], key: (row: T) => string): Array<[string, T[]]> {
  const groups = new Map<string, T[]>();
  for (const row of rows) {
    const value = key(row);
    groups.set(value, [...(groups.get(value) ?? []), row]);
  }
  return [...groups].sort(([a], [b]) => a.localeCompare(b));
}

/** Prices are averaged only inside compatible contracts, never at Book/PAIR. */
export function buildRecognitionHierarchy(input: WorkspaceTrade[]): RecognitionTreeNode[] {
  const trades = [...new Map(input.map((trade) => [trade.id, trade])).values()];
  const makeNode = (kind: RecognitionTreeNode["kind"], identity: unknown[], label: string, rows: WorkspaceTrade[], children: RecognitionTreeNode[]): RecognitionTreeNode => ({
    id: nodeId(kind, identity), kind, label, tradeIds: rows.map((trade) => trade.id).sort(), children,
    ...metrics(rows, kind === "contract" || kind === "trade"), ...(kind === "trade" ? { trade: rows[0]! } : {}),
  });
  return groupBy(trades, (trade) => JSON.stringify(text(trade.book))).map(([, bookTrades]) => {
    const book = text(bookTrades[0]!.book);
    const pairs = groupBy(bookTrades, (trade) => JSON.stringify(text(trade.pair))).map(([, pairTrades]) => {
      const pair = text(pairTrades[0]!.pair);
      const contracts = groupBy(pairTrades, (trade) => JSON.stringify(contractIdentity(trade))).map(([, contractTrades]) => {
        const first = contractTrades[0]!;
        const leaves = [...contractTrades].sort((a, b) => (a.tradedAt ?? "").localeCompare(b.tradedAt ?? "") || a.id.localeCompare(b.id))
          .map((trade) => makeNode("trade", [trade.id], recognitionTradeLabel(trade), [trade], []));
        return makeNode("contract", [book, pair, ...contractIdentity(first)], `${first.product} · ${first.period}`, contractTrades, leaves);
      });
      return makeNode("pair", [book, pair], pair ?? "Unpaired trades", pairTrades, contracts);
    });
    return makeNode("book", [book], book ?? "No book", bookTrades, pairs);
  });
}

export function findRecognitionNode(nodes: RecognitionTreeNode[], id: string): RecognitionTreeNode | undefined {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findRecognitionNode(node.children, id);
    if (found) return found;
  }
  return undefined;
}

export function flattenRecognitionNodes(nodes: RecognitionTreeNode[], expanded: ReadonlySet<string>, depth = 0): Array<{ node: RecognitionTreeNode; depth: number }> {
  return nodes.flatMap((node) => [{ node, depth }, ...(expanded.has(node.id) ? flattenRecognitionNodes(node.children, expanded, depth + 1) : [])]);
}

/** A matched result belongs to both source trades but must appear only once. */
export function itemsForTradeIds(items: ScheduleItem[], tradeIds: readonly string[]): ScheduleItem[] {
  const selected = new Set(tradeIds);
  return [...new Map(items.filter((item) => itemTradeIds(item).some((id) => selected.has(id))).map((item) => [item.itemId, item])).values()]
    .sort((a, b) => a.sourceMonth.localeCompare(b.sourceMonth) || a.itemId.localeCompare(b.itemId));
}

export function recognitionSelection(items: ScheduleItem[], trades: WorkspaceTrade[], tradeIds: readonly string[]) {
  const selected = new Set(tradeIds);
  const selectedItems = itemsForTradeIds(items, tradeIds);
  const linkedTradeIds = unique(selectedItems.flatMap(itemTradeIds)).sort();
  const outsideTradeIds = linkedTradeIds.filter((id) => !selected.has(id));
  return { items: selectedItems, linkedTradeIds, outsideTradeIds,
    counterpartTrades: trades.filter((trade) => outsideTradeIds.includes(trade.id)),
    unavailableTradeIds: outsideTradeIds.filter((id) => !trades.some((trade) => trade.id === id)) };
}

const blockerMessages: Record<string, string> = {
  source_trade_identity_missing: "The source trade reference is incomplete. Review the trade before changing recognition.",
  source_identity_ambiguous: "This result has more than one possible source. Review its matching before changing recognition.",
  source_ambiguous: "This result has more than one possible source. Reload and review its matching.",
  source_not_final: "This amount is still an estimate. A proposal can be saved, but approval must wait until the result is final.",
  legacy_reclass_overlap: "An existing recognition adjustment already affects this result. Review it before creating another schedule.",
  schedule_overlap: "An existing recognition schedule already affects this result. Review that schedule before changing it.",
  source_overlap: "Only one proposal per result can be included in the comparison. Choose which proposal to use.",
  source_economics_incomplete: "The source quantity or calculation is incomplete. Review the trade details.",
  source_fx_incomplete: "The exchange rate needed for this result is missing. Approval is unavailable until the rate is resolved.",
  source_date_incomplete: "The result's recognition date is incomplete. Review its source details.",
  source_stale: "The source result has changed. Reload and review the new amount before continuing.",
  source_missing: "This source result is no longer available. Reload the workspace.",
  active_schedule_changed: "The approved schedule has changed. Reload and review its replacement.",
  stale_view: "Recognition has changed since this preview. Refresh the comparison before continuing.",
  status_changed: "This proposal's status has changed. Reload its history before continuing.",
  schedule_writes_disabled: "Recognition changes are temporarily unavailable while rollout is being completed.",
};

export function recognitionBlockerMessage(blocker: string): string {
  return blockerMessages[blocker] ?? (/^[a-z][a-z0-9_]+$/.test(blocker)
    ? "This result needs review before recognition can be changed. Refresh the workspace or contact your company administrator."
    : blocker);
}

export type RecognitionProposalChoice = { kind: "draft"; draft: RecognitionScheduleDraft } | { kind: "pending"; entryId: string };
export type RecognitionProposalSelection = Record<string, RecognitionProposalChoice>;

/** The map replaces any previous choice for the item; no competing drafts sum. */
export function setRecognitionProposal(selection: RecognitionProposalSelection, itemId: string, choice: RecognitionProposalChoice | null): RecognitionProposalSelection {
  const next = { ...selection };
  if (choice) next[itemId] = choice;
  else delete next[itemId];
  return next;
}

/** Validate selection identity; the server alone calculates the monetary effect. */
export function resolveRecognitionProposals(items: ScheduleItem[], entries: RecognitionScheduleEntry[], selection: RecognitionProposalSelection): {
  drafts: RecognitionScheduleDraft[];
  pendingIds: string[];
  issues: Array<{ itemId: string; message: string }>;
} {
  const drafts: RecognitionScheduleDraft[] = [], pendingIds: string[] = [], issues: Array<{ itemId: string; message: string }> = [];
  for (const itemId of Object.keys(selection).sort()) {
    const issue = (message: string) => issues.push({ itemId, message });
    const candidates = items.filter((item) => item.itemId === itemId);
    if (candidates.length !== 1) { issue(recognitionBlockerMessage(candidates.length ? "source_ambiguous" : "source_missing")); continue; }
    const item = candidates[0]!, choice = selection[itemId]!;
    const pending = choice.kind === "pending" ? entries.find((entry) => entry.id === choice.entryId) : null;
    if (choice.kind === "pending" && (!pending || pending.itemId !== itemId || pending.status !== "pending")) {
      issue("The selected proposal is no longer pending for this result. Choose a current proposal."); continue;
    }
    if (pending?.stale) { issue(recognitionBlockerMessage("source_stale")); continue; }
    let draft: RecognitionScheduleDraft;
    try { draft = choice.kind === "draft" ? choice.draft : draftForEntry(pending!); }
    catch { issue("The selected proposal contains invalid schedule details. Open it and review the quantities and monthly allocation."); continue; }
    if (draft.itemId !== itemId || draft.sourceFingerprint !== item.sourceFingerprint) { issue(recognitionBlockerMessage("source_stale")); continue; }
    const active = entries.filter((entry) => entry.itemId === itemId && entry.status === "active");
    if (active.length > 1 || (active[0]?.id ?? null) !== (draft.replacesId ?? null)) { issue(recognitionBlockerMessage("active_schedule_changed")); continue; }
    const error = scheduleDraftError(item, draft);
    if (error) { issue(error); continue; }
    if (choice.kind === "draft") drafts.push(draft);
    else pendingIds.push(choice.entryId);
  }
  // A bad choice invalidates the complete comparison. Returning a partial list
  // would silently drop one of the user's requested changes.
  return { drafts: issues.length ? [] : drafts, pendingIds: issues.length ? [] : pendingIds, issues };
}
